import isEqual from 'react-fast-compare';

import {isChemSearch} from 'chemistry/ChemSearchUtils';
import {BenchlingAction} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';
import * as FilterUtil from 'search/FilterUtil';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {
  AsyncFetcherIsFetchedState,
  BLASTFilter,
  DEFAULT_ASYNC_FETCHER_IS_FETCHED_STATE,
  DEFAULT_LISTING_STATE,
  FilterErrors,
  FilterKey,
  GroupByKey,
  MultiValueFilter,
  SearchNextToken,
  SearchParams,
  SearchType,
  SortKey,
  UIListingState,
} from 'search/SearchConstants';
import uniqueId from 'util/uniqueId';

import {isBLASTSearch, isMultiQueryBLAST} from './blast/BLASTUtil';
import {getEmptyFilterErrors, validateFilters} from './filters/ValidateFilters';
import {finalizeListingSorts} from './getSortsOrDefault';
import {availableGroupKeysForFilters, narrowTypesForGroupKey} from './groupKeysByType';
import {SearchUIActionType} from './SearchUIReduxActions';
import {sanitizeSearchParams} from './SearchUtil';
import {isBLASTSort, isChemicalSimilaritySort} from './SortUtil';

export class SearchUISelector {
  constructor(readonly state: BenchlingState) {
    this.state = state;
  }

  getSearchSessionState(searchSessionId: string): SessionState | undefined {
    return this.state.ui.search.stateBySessionId[searchSessionId];
  }

  getSearchIdForSearchSession(searchSessionId: string): string {
    return this.state.ui.search.stateBySessionId[searchSessionId].searchId;
  }

  getParamsForSearchSession(searchSessionId: string): SearchParams {
    return this.state.ui.search.stateBySessionId[searchSessionId].searchParams;
  }

  getFilterErrorsForSearchSession(searchSessionId: string): FilterErrors {
    return this.state.ui.search.stateBySessionId[searchSessionId].filterErrors;
  }

  getActiveSavedSearchForSearchSession(searchSessionId: string): SavedSearch | undefined {
    return this.state.ui.search.activeSavedSearchBySessionId[searchSessionId];
  }

  getParamsForSearch(searchId: string): SearchParams {
    return this.state.ui.search.stateBySearchId[searchId].searchParams;
  }

  getChildrenPageSizeForSearch(searchId: string, multiExpand: boolean): number {
    const params = this.getParamsForSearch(searchId);
    if (multiExpand) {
      // Arbitrary limit to prevent excessive load when expanding multiple groups
      return Math.min(params.limit, 10);
    }
    return params.limit;
  }

  getTypeForSearch(searchId: string): SearchType {
    return this.state.ui.search.stateBySearchId[searchId].searchType;
  }

  getExpandedIdsForSearch(searchId: string): Array<string> {
    return this.state.ui.search.stateBySearchId[searchId].expandedIds;
  }

  getSelectedIdsForSearch(searchId: string): Array<string> {
    return this.state.ui.search.stateBySearchId[searchId].selectedIds;
  }

  getChildrenLoadingIdsForSearch(searchId: string): Array<string> {
    return this.state.ui.search.stateBySearchId[searchId].loadingChildrenIds;
  }

  getFilterErrorsForSearch(searchId: string): FilterErrors {
    return this.state.ui.search.stateBySearchId[searchId].filterErrors;
  }

  getRequestConsistencyForSearch(searchId: string): boolean | undefined {
    return this.state.ui.search.stateBySearchId[searchId].requestConsistency;
  }

  getListingStateForSearch(searchId: string): UIListingState {
    return this.state.ui.search.stateBySearchId[searchId].listingState;
  }

  getAsyncFetcherIsFetchedStateForSearch(searchId: string): AsyncFetcherIsFetchedState {
    return this.state.ui.search.stateBySearchId[searchId].asyncFetcherIsFetchedState;
  }
}

interface SessionState {
  searchParams: SearchParams;
  filterErrors: FilterErrors;
  searchId: string;
  offsetToNextToken: {
    [offset: number]: SearchNextToken;
  };
}
export interface SearchUIState {
  stateBySessionId: {
    [searchSessionId: string]: SessionState;
  };
  // The active saved search is tracked separately from `stateBySessionId` because it can be selected
  // before a search session has been initialized (e.g. navigating into a section for the first time by
  // picking a saved search). Keeping it out of `SessionState` avoids creating partial, invalid session
  // entries just to record the selection.
  activeSavedSearchBySessionId: {
    [searchSessionId: string]: SavedSearch | undefined;
  };
  stateBySearchId: {
    [searchId: string]: {
      expandedIds: Array<string>;
      loadingChildrenIds: Array<string>;
      selectedIds: Array<string>;
      searchParams: SearchParams;
      searchType: SearchType;
      filterErrors: FilterErrors;
      listingState: UIListingState;
      asyncFetcherIsFetchedState: AsyncFetcherIsFetchedState;
      requestConsistency?: boolean;
    };
  };
}

const INITIAL_STATE = {
  stateBySessionId: {},
  activeSavedSearchBySessionId: {},
  stateBySearchId: {},
};

function reduceForNewSearchParams(
  searchSessionId: string,
  searchType: SearchType,
  state: SearchUIState,
  searchParams: SearchParams,
  forPaging: boolean = false,
  newFilterErrors?: FilterErrors
): SearchUIState {
  const prevSessionState = state.stateBySessionId[searchSessionId];
  const filterErrors =
    newFilterErrors || prevSessionState?.filterErrors || getEmptyFilterErrors(searchParams.filters);

  searchParams = finalizeListingSorts(searchParams, searchType);

  const shouldStartSearch =
    !prevSessionState ||
    !isEqual(
      sanitizeSearchParams(searchParams, filterErrors),
      sanitizeSearchParams(prevSessionState.searchParams, prevSessionState.filterErrors)
    );

  // Reset the offset to 0 if we are triggering a new search, unless we have been explicitly told this is
  // a paging operation.
  const newOffset = shouldStartSearch && !forPaging ? 0 : searchParams.offset;
  const newNextToken = searchParams.groupBy
    ? state.stateBySessionId[searchSessionId].offsetToNextToken[newOffset]
    : null;

  const newSearchParams = {
    ...searchParams,
    offset: newOffset,
    nextToken: newNextToken,
  };

  let stateBySearchId = state.stateBySearchId;
  let searchId = prevSessionState?.searchId;

  if (shouldStartSearch) {
    searchId = uniqueId('search_');
    stateBySearchId = {
      ...stateBySearchId,
      [searchId]: {
        searchType,
        filterErrors,
        searchParams: newSearchParams,
        expandedIds: [],
        loadingChildrenIds: [],
        selectedIds: [],
        listingState: DEFAULT_LISTING_STATE,
        asyncFetcherIsFetchedState: DEFAULT_ASYNC_FETCHER_IS_FETCHED_STATE,
      },
    };
  }

  return {
    ...state,
    stateBySessionId: {
      ...state.stateBySessionId,
      [searchSessionId]: {
        ...state.stateBySessionId[searchSessionId],
        searchParams: newSearchParams,
        filterErrors,
        searchId,
        // Reset the next tokens used for paging if this is a new search that isn't a paging operation.
        offsetToNextToken:
          !shouldStartSearch || forPaging
            ? state.stateBySessionId[searchSessionId].offsetToNextToken
            : {0: null},
      },
    },
    stateBySearchId,
  };
}

export default function reduceSearchUI(
  state: SearchUIState = INITIAL_STATE,
  action: BenchlingAction
): SearchUIState {
  switch (action.type) {
    case SearchUIActionType.SEARCH_PARAMS_UPDATED: {
      const {searchSessionId, searchType, searchParams, filterErrors} = action;
      return {
        ...state,
        stateBySessionId: {
          ...state.stateBySessionId,
          [searchSessionId]: {
            ...state.stateBySessionId[searchSessionId],
            // SEARCH_PARAMS_UPDATED is only fired when completely overwriting the searchParameters, such as
            // when loading a saved search, navigating via a router event, or clearing search parameters. In
            // this case, any cached nextTokens are most likely not "correct" for the new search (and if they
            // are, we have no practical way of determining that). Therefore, if we are running a groupBy
            // search that pages by next token, we play it safe by ignoring the requested page and resetting
            // to the first page, as well as clearing the offsetToNextToken cache above.
            // NOTE: This also happens as part of SEARCH_STARTED - the fact that these are separate
            // actions means that we can't guarantee that the params passed to SEARCH_STARTED were pulled
            // from the session state updated here.
            searchParams: finalizeListingSorts(
              {
                ...searchParams,
                offset: searchParams.groupBy ? 0 : searchParams.offset,
                nextToken: searchParams.groupBy ? null : searchParams.nextToken,
              },
              searchType
            ),
            filterErrors,
          },
        },
      };
    }
    case SearchUIActionType.UPDATE_FILTERS: {
      const {searchSessionId, searchType, filters, selectedSchema} = action;

      const typesFilter = FilterUtil.getFilterByKey(filters, FilterKey.TYPES) as MultiValueFilter;
      const oldGroupBy = state.stateBySessionId[searchSessionId].searchParams.groupBy || null;
      // TODO(Search): Remove this cast after updating everything to use GroupByKey enum instead of
      // a string.
      const clearGroupBy =
        typesFilter &&
        oldGroupBy &&
        !availableGroupKeysForFilters(filters).includes(oldGroupBy.groupField as GroupByKey);

      const newSearchParams: SearchParams = {
        ...state.stateBySessionId[searchSessionId].searchParams,
        filters,
        groupBy: clearGroupBy ? null : oldGroupBy,
      };

      if (isBLASTSearch(newSearchParams)) {
        const blastFilter = FilterUtil.getFilterByKey(
          newSearchParams.filters,
          FilterKey.BLAST
        ) as BLASTFilter;
        // If we are adding a new BLAST filter, default the sort order to E value.
        if (!isBLASTSearch(state.stateBySessionId[searchSessionId].searchParams)) {
          newSearchParams.sorts = [{key: SortKey.SORT_BLAST_E_VALUE, reverse: false}];
        }

        if (isMultiQueryBLAST(blastFilter)) {
          newSearchParams.groupBy = {
            groupField: GroupByKey.BLAST_QUERY,
            childOffset: 0,
            childLimit: newSearchParams.limit,
          };
        }
      } else if (isBLASTSort(state.stateBySessionId[searchSessionId].searchParams.sorts)) {
        newSearchParams.sorts = [];
      }

      if (isChemSearch(newSearchParams)) {
        // If we are adding a new chem filter, default the sort order to similarity.
        if (!isChemSearch(state.stateBySessionId[searchSessionId].searchParams)) {
          newSearchParams.sorts = [{key: SortKey.SORT_CHEMICAL_SIMILARITY, reverse: false}];
        }
      } else if (isChemicalSimilaritySort(state.stateBySessionId[searchSessionId].searchParams.sorts)) {
        newSearchParams.sorts = [];
      }

      const filterErrors = validateFilters(newSearchParams.filters, selectedSchema);

      return reduceForNewSearchParams(
        searchSessionId,
        searchType,
        state,
        newSearchParams,
        false,
        filterErrors
      );
    }
    case SearchUIActionType.UPDATE_QUERY_STRING: {
      const {searchSessionId, searchType, query} = action;
      const currentSearchParams = state.stateBySessionId[searchSessionId].searchParams;

      let sorts = currentSearchParams.sorts;
      if (!isChemSearch(currentSearchParams)) {
        // If searching with query, we default to a relevance sort.
        if (query) {
          sorts = [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}];
        } else {
          sorts = sorts.filter((sort) => sort.key !== SortKey.SORT_RELEVANCE_KEY);
        }
      }

      const newSearchParams: SearchParams = {...currentSearchParams, sorts, query};
      return reduceForNewSearchParams(searchSessionId, searchType, state, newSearchParams);
    }
    case SearchUIActionType.UPDATE_PAGE_SIZE: {
      const {searchSessionId, searchType, limit} = action;
      const newSearchParams = {
        ...state.stateBySessionId[searchSessionId].searchParams,
        limit,
      };
      if (isBLASTSearch(newSearchParams)) {
        const blastFilter = FilterUtil.getFilterByKey(
          newSearchParams.filters,
          FilterKey.BLAST
        ) as BLASTFilter;
        if (isMultiQueryBLAST(blastFilter)) {
          newSearchParams.groupBy = {
            groupField: GroupByKey.BLAST_QUERY,
            childOffset: 0,
            childLimit: limit,
          };
        }
      }

      return reduceForNewSearchParams(searchSessionId, searchType, state, newSearchParams);
    }
    case SearchUIActionType.UPDATE_SORT_ORDER: {
      const {searchSessionId, searchType, sorts} = action;
      const newSearchParams = {
        ...state.stateBySessionId[searchSessionId].searchParams,
        sorts,
      };
      return reduceForNewSearchParams(searchSessionId, searchType, state, newSearchParams);
    }
    case SearchUIActionType.UPDATE_GROUP_BY: {
      const {searchSessionId, searchType, groupField} = action;
      const oldFilters = state.stateBySessionId[searchSessionId].searchParams.filters;
      const typesFilter = FilterUtil.getFilterByKey(oldFilters, FilterKey.TYPES) as MultiValueFilter;

      // Filter errors are indexed by position, so narrowing TYPES must preserve filter order.
      const newFilters = typesFilter
        ? FilterUtil.replaceByKey(
            oldFilters,
            // TODO(Search): Remove this cast after updating everything to use GroupByKey enum instead of
            // a string.
            narrowTypesForGroupKey(typesFilter, groupField as GroupByKey)
          )
        : oldFilters;

      const newSearchParams = {
        ...state.stateBySessionId[searchSessionId].searchParams,
        filters: newFilters,
        groupBy: groupField ? {groupField, childOffset: 0, childLimit: 0} : null,
      };
      return reduceForNewSearchParams(searchSessionId, searchType, state, newSearchParams);
    }
    case SearchUIActionType.PAGE_CHANGED: {
      const {searchSessionId, searchType, offset} = action;
      const sessionState = state.stateBySessionId[searchSessionId];
      // This is a safety check to ensure that the UI doesn't inadvertently advance multiple pages when using
      // nextToken based searching, which we can't do since we won't know those nextTokens until the results
      // for intermediate pages are returned. We simply no-op in that case and assume that the last valid search
      // operation will eventually fill these in for the user to retry.
      if (sessionState.searchParams.groupBy && !sessionState.offsetToNextToken.hasOwnProperty(offset)) {
        return state;
      }
      const newSearchParams = {
        ...state.stateBySessionId[searchSessionId].searchParams,
        offset,
      };
      return reduceForNewSearchParams(searchSessionId, searchType, state, newSearchParams, true);
    }
    // Associate a new searchId with the connected component identified by searchSessionId, causing the
    // connected component to fetch new search results.
    //
    // searchParams and searchType are used by SearchResultsModelManager to fetch for those results.
    case SearchUIActionType.SEARCH_STARTED: {
      const {searchSessionId, searchId, searchType, searchParams, filterErrors} = action;
      return {
        ...state,
        stateBySessionId: {
          ...state.stateBySessionId,
          [searchSessionId]: {
            ...state.stateBySessionId[searchSessionId],
            searchId,
            offsetToNextToken: {0: null},
          },
        },
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {
            searchType,
            filterErrors,
            // SEARCH_STARTED is only fired when completely overwriting the searchParameters, such as when
            // loading a saved search, navigating via a router event, or clearing search parameters. In this case,
            // any cached nextTokens are most likely not "correct" for the new search (and if they are, we have
            // no practical way of determining that). Therefore, if we are running a groupBy search that pages
            // by next token, we play it safe by ignoring the requested page and resetting to the first page,
            // as well as clearing the offsetToNextToken cache above.
            // NOTE: This also happens as part of SEARCH_PARAMS_UPDATED - the fact that these are separate
            // actions means that we can't guarantee that the params passed to SEARCH_STARTED were pulled
            // from the session state.
            searchParams: finalizeListingSorts(
              {
                ...searchParams,
                offset: searchParams.groupBy ? 0 : searchParams.offset,
                nextToken: searchParams.groupBy ? null : searchParams.nextToken,
              },
              searchType
            ),
            expandedIds: [],
            loadingChildrenIds: [],
            selectedIds: [],
            listingState: DEFAULT_LISTING_STATE,
            asyncFetcherIsFetchedState: DEFAULT_ASYNC_FETCHER_IS_FETCHED_STATE,
          },
        },
      };
    }
    // Associate a new searchId with the connected component identified by searchSessionId, causing the
    // connected component to fetch new search results. I.e., this resets the specified search cache.
    case SearchUIActionType.SEARCH_FORCE_REFRESHED: {
      const {searchSessionId, searchId, clearSelection} = action;
      const oldSessionState = state.stateBySessionId[searchSessionId];
      // If a search has not yet been triggered, there is no need to refresh.
      if (oldSessionState == null) {
        return state;
      }
      const newSelection = clearSelection
        ? []
        : [...state.stateBySearchId[oldSessionState.searchId].selectedIds];
      return {
        ...state,
        stateBySessionId: {
          ...state.stateBySessionId,
          [searchSessionId]: {
            ...oldSessionState,
            searchId,
          },
        },
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {
            ...state.stateBySearchId[oldSessionState.searchId],
            selectedIds: newSelection,
            requestConsistency: true,
          },
        },
      };
    }
    case SearchUIActionType.SELECTED_IDS_UPDATED: {
      const {searchId, selectedIds} = action;
      return {
        ...state,
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {...state.stateBySearchId[searchId], selectedIds},
        },
      };
    }
    case SearchUIActionType.UPDATE_ACTIVE_SAVED_SEARCH: {
      const {activeSavedSearch, searchSessionId} = action;
      const activeSavedSearchBySessionId = {...state.activeSavedSearchBySessionId};
      if (activeSavedSearch == null) {
        delete activeSavedSearchBySessionId[searchSessionId];
      } else {
        activeSavedSearchBySessionId[searchSessionId] = activeSavedSearch;
      }
      return {
        ...state,
        activeSavedSearchBySessionId,
      };
    }
    case SearchUIActionType.EXPANDED_IDS_UPDATED: {
      const {searchId, expandedIds} = action;
      return {
        ...state,
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {...state.stateBySearchId[searchId], expandedIds},
        },
      };
    }
    case SearchUIActionType.CHILD_FETCH_ID_ADDED: {
      const {searchId, groupIds} = action;
      const idsToAdd = groupIds.filter(
        (groupId) => !state.stateBySearchId[searchId].loadingChildrenIds.includes(groupId)
      );
      return {
        ...state,
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {
            ...state.stateBySearchId[searchId],
            loadingChildrenIds: [...state.stateBySearchId[searchId].loadingChildrenIds, ...idsToAdd],
          },
        },
      };
    }
    case SearchUIActionType.CHILD_FETCH_ID_REMOVED: {
      const {searchId, groupIds} = action;
      const loadingChildrenIds = state.stateBySearchId[searchId].loadingChildrenIds.filter(
        (groupId) => !groupIds.includes(groupId)
      );
      return {
        ...state,
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {...state.stateBySearchId[searchId], loadingChildrenIds},
        },
      };
    }
    case SearchUIActionType.UPDATE_VIEW_SELECTION: {
      const {searchId, viewSelection} = action;
      return {
        ...state,
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {
            ...state.stateBySearchId[searchId],
            listingState: {
              ...state.stateBySearchId[searchId].listingState,
              viewSelection,
            },
          },
        },
      };
    }
    case SearchUIActionType.UPDATE_REQUEST_CONSISTENCY: {
      const {searchId, requestConsistency} = action;
      return {
        ...state,
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {
            ...state.stateBySearchId[searchId],
            requestConsistency,
          },
        },
      };
    }
    case SearchUIActionType.UPDATE_ASYNC_FETCHER_IS_FETCHED_STATE_ACTION: {
      const {searchId, flagName, value} = action;
      return {
        ...state,
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {
            ...state.stateBySearchId[searchId],
            asyncFetcherIsFetchedState: {
              ...state.stateBySearchId[searchId].asyncFetcherIsFetchedState,
              [flagName]: value,
            },
          },
        },
      };
    }
    case SearchUIActionType.UPDATE_GROUP_BY_SELECTION: {
      const {searchId, groupBySelection} = action;
      return {
        ...state,
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {
            ...state.stateBySearchId[searchId],
            listingState: {
              ...state.stateBySearchId[searchId].listingState,
              groupBySelection,
            },
          },
        },
      };
    }
    case SearchUIActionType.UPDATE_MULTIQUERY_BLAST_OFFSET: {
      const {searchId, multiqueryBlastOffset} = action;
      return {
        ...state,
        stateBySearchId: {
          ...state.stateBySearchId,
          [searchId]: {
            ...state.stateBySearchId[searchId],
            listingState: {
              ...state.stateBySearchId[searchId].listingState,
              multiqueryBlastOffset,
            },
          },
        },
      };
    }
    case SearchUIActionType.NEXT_TOKEN_ADDED: {
      const {searchId, nextToken} = action;
      const sessionIdForSearch = Object.keys(state.stateBySessionId).find(
        (sessionId) => state.stateBySessionId[sessionId].searchId === searchId
      );
      if (!sessionIdForSearch) {
        return state;
      }
      const searchParamsForSearch = state.stateBySearchId[searchId].searchParams;
      const offset = searchParamsForSearch.offset + searchParamsForSearch.limit;
      return {
        ...state,
        stateBySessionId: {
          ...state.stateBySessionId,
          [sessionIdForSearch]: {
            ...state.stateBySessionId[sessionIdForSearch],
            offsetToNextToken: {
              ...state.stateBySessionId[sessionIdForSearch].offsetToNextToken,
              [offset]: nextToken,
            },
          },
        },
      };
    }
    default: {
      return state;
    }
  }
}
